<template>
  <div class="image-editor">
    <div class="editor-layout">
      <!-- Lienzo -->
      <div class="image-container" ref="containerRef">
        <v-stage ref="stageRef" :config="stageSize">
          <v-layer>
            <v-image
              v-if="mainImage.value"
              :config="{
                image: mainImage.value,
                width: stageSize.width,
                height: stageSize.height,
                listening: false
              }"
            />
            <v-image
              v-if="watermarkProps.image"
              :config="{
                image: watermarkProps.image,
                x: watermarkProps.x,
                y: watermarkProps.y,
                draggable: true,
                width: watermarkProps.width,
                height: watermarkProps.height,
                opacity: watermarkProps.opacity
              }"
              @dragmove="handleDrag"
            />
          </v-layer>
        </v-stage>
      </div>

      <!-- Panel de controles -->
      <div class="control-panel">
        <label :class="['custom-file-upload', { loaded: isMainImageLoaded }]">
          <input type="file" @change="loadMainImage" accept="image/*" />
          Cargar Imagen de Fondo
        </label>

        <label :class="['custom-file-upload', { loaded: !!watermarkProps.image }]">
          <input type="file" @change="loadWatermark" accept="image/*" />
          Cargar Marca de Agua
        </label>

        <label for="opacityRange">Ajustar Opacidad:</label>
        <input
          id="opacityRange"
          type="range"
          min="0" max="1" step="0.01"
          v-model="watermarkSettings.opacity"
          @input="updateOpacity"
        />

        <label for="sizeRange">Ajustar Tamaño de la Marca de Agua:</label>
        <input
          id="sizeRange"
          type="range"
          min="0" max="1" step="0.01"
          v-model="watermarkSettings.size"
          @input="updateSize"
        />

        <!-- Presets de tamaño -->
        <div class="quick">
          <div class="quick-title">Tamaño rápido</div>
          <div class="chip-group">
            <button class="chip" @click="setSizePercent(0.25)">25%</button>
            <button class="chip" @click="setSizePercent(0.30)">30%</button>
            <button class="chip" @click="setSizePercent(0.35)">35%</button>
            <button class="chip" @click="setSizePercent(0.50)">50%</button>
          </div>
        </div>

        <!-- Presets de posición -->
        <div class="quick">
          <div class="quick-title">Posición</div>
          <div class="chip-group">
            <button class="chip icon" aria-label="Arriba izquierda" @click="positionWatermark('tl')">↖</button>
            <button class="chip icon" aria-label="Arriba derecha"  @click="positionWatermark('tr')">↗</button>
            <button class="chip icon" aria-label="Centro"          @click="positionWatermark('c')">⬤</button>
            <button class="chip icon" aria-label="Abajo izquierda" @click="positionWatermark('bl')">↙</button>
            <button class="chip icon" aria-label="Abajo derecha"   @click="positionWatermark('br')">↘</button>
          </div>
          <div class="hint">
            Tip: además de los botones, puedes hacer clic sobre la marca de agua y arrastrarla para
            posicionarla libremente. Los presets colocan la marca con un margen de seguridad del 5%
            para evitar pegarla a las orillas.
          </div>

        </div>

        <button class="custom-button" @click="downloadImage">Descargar Imagen</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import { VStage, VLayer, VImage } from 'vue-konva'

export default {
  components: { VStage, VLayer, VImage },
  setup () {
    const mainImage = new Image()
    const isMainImageLoaded = ref(false)
    const stageSize = ref({ width: 800, height: 600 })
    const stageRef = ref(null)
    const containerRef = ref(null)

    const watermarkSettings = reactive({
      opacity: 0.5,
      size: 0.5, // 0..1 del ancho del lienzo
    })

    const watermarkProps = reactive({
      x: 0, y: 0,
      opacity: 0.5,
      width: 200, height: 200,
      image: null,
    })

    function loadMainImage (event) {
      const file = event.target.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = e => {
        const img = new Image()
        img.src = e.target.result
        img.onload = () => {
          mainImage.value = img
          adjustStageSize()
          isMainImageLoaded.value = true
        }
      }
      reader.readAsDataURL(file)
    }

    const adjustStageSize = () => {
      if (!containerRef.value || !mainImage.value?.width) return
      const maxW = containerRef.value.clientWidth * 0.95
      const maxH = containerRef.value.clientHeight * 0.95
      const ar = mainImage.value.width / mainImage.value.height
      let w = maxW
      let h = w / ar
      if (h > maxH) { h = maxH; w = h * ar }
      stageSize.value = { width: w, height: h }
    }

    function loadWatermark (event) {
      const file = event.target.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = e => {
        const img = new Image()
        img.onload = () => {
          watermarkProps.image = img
          const ar = img.width / img.height
          let maxW = Math.min(stageSize.value.width * 0.5, img.width)
          let maxH = maxW / ar
          if (maxH > stageSize.value.height * 0.5) {
            maxH = stageSize.value.height * 0.5
            maxW = maxH * ar
          }
          watermarkProps.width = maxW
          watermarkProps.height = maxH
          positionWatermark('c') // al centro por defecto
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    }

    function updateOpacity () {
      watermarkProps.opacity = watermarkSettings.opacity
    }

    function ensureWithinBounds () {
      // Evita salirse del lienzo tras cambios
      watermarkProps.x = Math.max(0, Math.min(stageSize.value.width  - watermarkProps.width,  watermarkProps.x))
      watermarkProps.y = Math.max(0, Math.min(stageSize.value.height - watermarkProps.height, watermarkProps.y))
    }

    function updateSize () {
      if (!watermarkProps.image) return
      const ar = watermarkProps.image.width / watermarkProps.image.height
      let newW = stageSize.value.width * watermarkSettings.size
      let newH = newW / ar

      if (newH > stageSize.value.height * watermarkSettings.size) {
        newH = stageSize.value.height * watermarkSettings.size
        newW = newH * ar
      }

      watermarkProps.width = Math.max(1, newW)
      watermarkProps.height = Math.max(1, newH)
      ensureWithinBounds()
    }

    function setSizePercent (p) {
      watermarkSettings.size = p
      updateSize()
    }

    function positionWatermark (pos) {
      if (!watermarkProps.image) return
      const mx = stageSize.value.width  * 0.05 // 5% margen
      const my = stageSize.value.height * 0.05

      if (pos === 'c') {
        watermarkProps.x = (stageSize.value.width  - watermarkProps.width)  / 2
        watermarkProps.y = (stageSize.value.height - watermarkProps.height) / 2
      } else if (pos === 'tl') {
        watermarkProps.x = mx
        watermarkProps.y = my
      } else if (pos === 'tr') {
        watermarkProps.x = stageSize.value.width - watermarkProps.width - mx
        watermarkProps.y = my
      } else if (pos === 'bl') {
        watermarkProps.x = mx
        watermarkProps.y = stageSize.value.height - watermarkProps.height - my
      } else if (pos === 'br') {
        watermarkProps.x = stageSize.value.width - watermarkProps.width - mx
        watermarkProps.y = stageSize.value.height - watermarkProps.height - my
      }
      ensureWithinBounds()
    }

    function handleDrag (e) {
      const node = e.target
      const box = node.getClientRect()
      const nx = Math.max(0, Math.min(stageSize.value.width  - box.width,  node.x()))
      const ny = Math.max(0, Math.min(stageSize.value.height - box.height, node.y()))
      node.x(nx); node.y(ny)
      watermarkProps.x = nx; watermarkProps.y = ny
    }

    function downloadImage () {
      if (!stageRef.value) return
      const ow = stageSize.value.width
      const oh = stageSize.value.height
      let tw, th
      if (ow >= oh) { tw = 800; th = (oh / ow) * 800 } else { th = 800; tw = (ow / oh) * 800 }

      const stage = stageRef.value.getStage()
      stage.width(tw); stage.height(th)
      stage.scale({ x: tw / ow, y: th / oh })

      const uri = stage.toDataURL()
      const link = document.createElement('a')
      link.download = 'composite-image.png'
      link.href = uri
      link.click()

      stage.width(ow); stage.height(oh)
      stage.scale({ x: 1, y: 1 })
    }

    const handleResize = () => { adjustStageSize(); ensureWithinBounds() }
    onMounted(() => window.addEventListener('resize', handleResize))
    onBeforeUnmount(() => window.removeEventListener('resize', handleResize))

    watch(mainImage, (img) => { if (img) adjustStageSize() })

    return {
      // state
      mainImage, isMainImageLoaded, stageSize, stageRef, containerRef,
      watermarkSettings, watermarkProps,
      // actions
      loadMainImage, loadWatermark,
      updateOpacity, updateSize, setSizePercent,
      positionWatermark, handleDrag, downloadImage,
    }
  },
}
</script>

<style lang="sass" scoped>
.image-editor
  display: flex
  flex-direction: column
  align-items: center
  width: 95%
  max-width: 1200px
  margin: 24px auto
  color: var(--text)

.editor-layout
  display: flex
  flex-direction: column
  width: 100%
  gap: 18px

.image-container
  display: flex
  justify-content: center
  align-items: center
  width: 100%
  overflow: hidden
  box-sizing: border-box
  border-radius: 20px
  background: linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.0)), var(--panel)
  border: 1px solid var(--border)
  box-shadow: 0 20px 60px rgba(0,0,0,.35)
  padding: 10px

.control-panel
  display: flex
  flex-direction: column
  gap: 14px
  width: 100%
  margin: 0 auto
  background: var(--panel-2)
  border: 1px solid var(--border)
  border-radius: 16px
  padding: 16px

/* grupos rápidos */
.quick
  margin-top: 4px

.quick-title
  color: var(--muted)
  font-size: .85rem
  margin-bottom: 6px
  text-align: left

.chip-group
  display: flex
  flex-wrap: wrap
  gap: 8px

.chip
  appearance: none
  border: 1px solid var(--border)
  background: #1b2232
  color: var(--text)
  padding: 8px 12px
  border-radius: 999px
  font-weight: 700
  font-size: .9rem
  cursor: pointer
  transition: .15s ease
  &:hover
    border-color: #364052
    transform: translateY(-1px)
  &.icon
    width: 40px
    height: 40px
    display: inline-flex
    align-items: center
    justify-content: center
    padding: 0
    font-size: 1rem

.hint
  margin-top: 6px
  font-size: .78rem
  color: var(--muted)

/* botones de archivo */
.custom-file-upload
  display: inline-flex
  align-items: center
  justify-content: center
  padding: 10px 14px
  cursor: pointer
  border-radius: 12px
  background-color: #1b2232
  color: var(--text)
  font-weight: 600
  text-align: center
  font-size: .95rem
  border: 1px solid var(--border)
  transition: transform .15s ease, border-color .15s ease, background-color .15s ease
  user-select: none
  input[type="file"]
    display: none
  &:hover
    border-color: #364052
    background-color: #20283b
    transform: translateY(-1px)
  &.loaded
    background: var(--brand-600)
    border-color: transparent
    &:hover
      background: var(--brand-700)

/* sliders */
label
  text-align: left
  font-size: .9rem
  color: var(--muted)

input[type="range"]
  -webkit-appearance: none
  width: 100%
  height: 10px
  border-radius: 999px
  background: #242b3a
  outline: none
  cursor: pointer
  border: 1px solid var(--border)
  margin: 4px 0 10px 0
  &::-webkit-slider-thumb
    -webkit-appearance: none
    appearance: none
    width: 22px
    height: 22px
    border-radius: 50%
    background: var(--brand-600)
    border: 2px solid #fff2
    box-shadow: 0 4px 14px rgba(124,77,255,.35)
  &::-moz-range-thumb
    width: 22px
    height: 22px
    border-radius: 50%
    background: var(--brand-600)
    border: 2px solid #fff2
    box-shadow: 0 4px 14px rgba(124,77,255,.35)

/* botón principal */
.custom-button
  padding: 12px 16px
  margin-top: 8px
  background: var(--brand-600)
  color: #fff
  font-size: 15px
  font-weight: 700
  border: none
  border-radius: 12px
  cursor: pointer
  box-shadow: 0 10px 24px rgba(124,77,255,.28)
  transition: background-color .15s ease, transform .15s ease
  &:hover
    background: var(--brand-700)
    transform: translateY(-1px)
  &:focus
    outline: 2px solid #ffffff33
    outline-offset: 2px

/* Desktop: usar grid y centrar verticalmente el panel */
@media (min-width: 900px)
  .editor-layout
    display: grid
    grid-template-columns: 1fr minmax(340px, 420px)
    align-items: center  /* <-- centrado vertical del panel */
    gap: 20px

  .image-container, .control-panel
    width: 100%

  .image-container
    aspect-ratio: 1 / 1
    max-width: 820px

    div
      width: 100%
      display: flex
      justify-content: center
      align-items: center
      .konvajs-content
        width: 100%
        canvas
          width: 100%
</style>
